import {showNotification} from 'platform/components';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useLazyGetEventQuery} from '@dms/api/core';
import {useDownloadServiceGalleryMutation, useLazyDownloadGalleryQuery} from '@dms/api/sales';
import {
  BatchDownloadEventIdArg,
  BatchDownloadSaleVehicleEventIdArg,
  BatchDownloadServiceGalleryEventIdArg,
  BulkDownloadApiResponse,
} from '@dms/api/shared';
import {useDownloadSaleVehicleGalleryMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {notificationTypes} from '../constants/notificationTypes';
import {downloadFile} from '../utils/downloadFile';
import {handleApiError} from '../utils/handleApiError';

export function useBatchDownload() {
  const [eventData, setEventData] = useState<BulkDownloadApiResponse | undefined>();
  const [initiatedEventIds, setInitiatedEventIds] = useState<Set<string>>(new Set());

  const [triggerDownloadEvent, {isLoading: isDownloadTriggerLoading, isError: hasDownloadError}] =
    useLazyDownloadGalleryQuery();

  const [
    triggerSaleVehicleDownloadEvent,
    {isLoading: isSaleVehicleDownloadTriggerLoading, isError: hasSaleVehicleDownloadError},
  ] = useDownloadSaleVehicleGalleryMutation();

  const [
    triggerServiceAlbumDownloadEvent,
    {isLoading: isServiceVehicleAlbumTriggerLoading, isError: hasServiceAlbumDownloadError},
  ] = useDownloadServiceGalleryMutation();

  const [getDownloadEvent, {isLoading: isDownloadEventLoading}] = useLazyGetEventQuery();

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== notificationTypes.BATCH_DOWNLOAD_STATE_CHANGED) {
      return;
    }

    const eventId = notification.payload.eventId;
    if (!initiatedEventIds.has(eventId)) {
      return;
    }

    getDownloadEvent({eventId})
      .unwrap()
      .then((downloadEvent) => {
        const url = new URL(notification.payload.bulkObjectDownloadUrl ?? '');
        url.searchParams.set('filename', downloadEvent.data.bulkName ?? 'batch.zip');

        downloadFile(url.toString());

        setEventData(downloadEvent);
        setInitiatedEventIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(eventId);
          return newSet;
        });
      })
      .catch(handleApiError);
  });

  const handleDownload = (downloadArgs: BatchDownloadEventIdArg) => {
    setEventData(undefined);

    return triggerDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        // throws the you need to use .unwrap() error here, due to bad rule config
        // eslint-disable-next-line no-restricted-syntax
        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));

        getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            setEventData(downloadEvent);
          })
          .catch(handleApiError);
      })
      .catch(handleApiError);
  };

  const handleSaleVehicleDownload = (downloadArgs: BatchDownloadSaleVehicleEventIdArg) => {
    setEventData(undefined);

    return triggerSaleVehicleDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));

        return getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            setEventData(downloadEvent);
            return downloadEvent;
          })
          .catch(handleApiError);
      })
      .catch((error: Error & {data?: {errors?: {code?: string}[]}}) => {
        if (error?.data?.errors?.[0]?.code === 'DMS-SV-AL-0013') {
          showNotification.error(i18n.t('entity.photo.notifications.noPhotosInAlbums'));
          return;
        }
        handleApiError(error);
      });
  };

  const handleServiceAlbumDownload = (downloadArgs: BatchDownloadServiceGalleryEventIdArg) => {
    setEventData(undefined);

    return triggerServiceAlbumDownloadEvent(downloadArgs)
      .unwrap()
      .then((triggeredEvent) => {
        if (isNil(triggeredEvent?.eventId)) {
          showNotification.error(i18n.t('general.notifications.notFound'));
          return;
        }

        // throws the you need to use .unwrap() error here, due to bad rule config
        // eslint-disable-next-line no-restricted-syntax
        setInitiatedEventIds((prev) => new Set(prev).add(triggeredEvent.eventId));

        return getDownloadEvent({eventId: triggeredEvent.eventId})
          .unwrap()
          .then((downloadEvent) => {
            setEventData(downloadEvent);
            return downloadEvent;
          })
          .catch(handleApiError);
      })
      .catch(handleApiError);
  };

  return {
    handleDownload,
    handleSaleVehicleDownload,
    handleServiceAlbumDownload,
    eventData,
    isLoading:
      isDownloadTriggerLoading ||
      isDownloadEventLoading ||
      isSaleVehicleDownloadTriggerLoading ||
      isServiceVehicleAlbumTriggerLoading,
    isError: hasDownloadError || hasSaleVehicleDownloadError || hasServiceAlbumDownloadError,
    isStatePending: eventData?.state === 'PENDING',
  };
}
